import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Loads one thing and keeps loading, error and data together. `deps` re-run
 * it; `reload` re-runs it after a write. A late answer to an old request
 * never overwrites a newer one.
 */
export function useLoad<T>(load: () => Promise<T>, deps: unknown[]) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const ticket = useRef(0);
  const run = useCallback(load, deps);

  const reload = useCallback(() => {
    const mine = ++ticket.current;
    setLoading(true);
    run()
      .then((d) => {
        if (mine !== ticket.current) return;
        setData(d);
        setError(null);
      })
      .catch((e: unknown) => {
        if (mine !== ticket.current) return;
        setError(e instanceof Error ? e.message : "Something went wrong.");
      })
      .finally(() => {
        if (mine === ticket.current) setLoading(false);
      });
  }, [run]);

  useEffect(reload, [reload]);
  return { data, error, loading, reload, setData };
}
